import { Info } from 'lucide-react';

import type { CascadeMetrics, Calibration } from '@/types/cascade-report';
import { decimal, money, num, pct } from '../format';
import { METRICS, type MetricKey } from '../metrics';

interface Card {
    metric: MetricKey | null;
    label: string;
    value: string;
    /** Uma linha curta. A explicação longa mora na legenda e no tooltip. */
    hint: string;
    /** Marca o número que não é o que parece. */
    warn?: boolean;
}

export function SummaryCards({
    totals,
    calibration,
}: {
    totals: CascadeMetrics;
    calibration?: Calibration;
}) {
    const confiavel = totals.cascades_reliable;
    // A margem é a pior dispersão entre os blocos calibrados. Mostrá-la ao lado
    // do valor é o que separa uma projeção de um número inventado.
    const margem =
        calibration?.calibrated && calibration.margin !== null
            ? `${(calibration.margin * 100).toFixed(0)}%`
            : null;
    const dias = calibration?.days ?? 0;

    const cards: Card[] = [
        {
            metric: 'cascades',
            label: METRICS.cascades.label,
            value: confiavel ? num(totals.cascades) : '—',
            hint: confiavel ? 'rodaram no período' : 'contagem incompleta',
        },
        {
            metric: 'fill_rate',
            label: METRICS.fill_rate.label,
            value: confiavel ? pct(totals.fill_rate) : '—',
            hint: confiavel ? `${num(totals.wins)} venderam` : 'reprocesse o rollup',
        },
        {
            // O número de dinheiro. Vem primeiro porque é o que a pergunta
            // "como está hoje" quer saber — o nominal dos pisos ficou de fora
            // dos cards e continua na tabela e na legenda.
            metric: 'revenue_expected',
            label: METRICS.revenue_expected.label,
            value: money(totals.revenue_expected),
            hint: margem
                ? `± ${margem} · calibrado em ${dias} dia${dias === 1 ? '' : 's'}`
                : 'sem calibração no período',
            warn: !margem,
        },
        {
            metric: 'ecpm_expected',
            label: METRICS.ecpm_expected.label,
            value: money(totals.ecpm_expected),
            hint: `${num(totals.viewables)} exibições`,
        },
        {
            metric: 'requests_per_sale',
            label: 'Sol./venda',
            value: totals.wins ? decimal(totals.requests_per_sale) : '—',
            hint: `${num(totals.attempts)} solicitações`,
        },
        {
            metric: 'viewable_rate',
            label: METRICS.viewable_rate.label,
            value: totals.wins ? pct(totals.viewable_rate) : '—',
            hint: `${num(totals.viewables)} vistas`,
            warn: true,
        },
    ];

    return (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-6">
            {cards.map((card) => (
                <div
                    key={card.label}
                    className="rounded-lg border border-gray-200 bg-white p-4"
                    title={card.metric ? METRICS[card.metric].long : undefined}
                >
                    <div className="flex items-center gap-1 text-xs font-medium text-gray-500">
                        <span className="truncate">{card.label}</span>
                        {card.warn && <Info className="h-3 w-3 shrink-0 text-amber-500" />}
                    </div>
                    <div className="mt-1 truncate text-2xl font-semibold tabular-nums text-gray-900">
                        {card.value}
                    </div>
                    <div className="mt-1 truncate text-[11px] text-gray-400">{card.hint}</div>
                </div>
            ))}
        </div>
    );
}
